import Modal from "react-bootstrap/Modal";
import {Alert, Button, getFullName, Loading, ProfilePicture} from "fearless-common-ui";
import React, {PropsWithChildren, useState} from "react";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import {Check} from "react-bootstrap-icons";
import {BouncerAbility, concepts, Group, GroupRole, User} from "fearless-common";
import {getRoleLabel} from "../../libs/backendEnums";
import useGet from "../hooks/useGet";
import {backendEndpoints} from "../../libs/backendEndpoints";
import axios from "axios";
import {toast} from "react-hot-toast";

type GroupWithUsers = (Group & { users?: Array<User & { pivot: { role: GroupRole }}> });

function PermissionTable({ children, header }: PropsWithChildren<{ header: string }>) {
  return (
    <table className="table table-sm">
      <thead>
      <tr className="text-center">
        <th className="w-50 text-start align-middle">{header}</th>
        <th className="text-sm">Import<br/>{concepts.Group.toLowerCase()} files</th>
        <th className="text-sm">Edit<br/>{concepts.Group.toLowerCase()} files</th>
      </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}

interface GroupPermissions {
  per_user: Record<User['id'], Record<BouncerAbility, boolean>>;
  per_role: Record<GroupRole, Record<BouncerAbility, boolean>>;
}

function PermissionCheckbox({disabled, checked, data, groupId, reloadCallback}: {
  disabled?: boolean;
  checked?: boolean;
  data?: { user_id: number; ability: BouncerAbility } | { role: GroupRole; ability: BouncerAbility };
  groupId?: number;
  reloadCallback?: () => {},
}) {
  const [isProcessing, setProcessing] = useState(false);
  const onChange = data && groupId?
    () => {
      setProcessing(true);
      return axios
        .put(backendEndpoints.updateGroupPermissions(groupId), data)
        .then((result) => {
          toast.success(result.data.message);
          return result;
        })
        .catch((e) => {
          toast.error(e.message);
        })
        .finally(() => {
          setProcessing(false);
          if (reloadCallback) reloadCallback();
        })
    }
    : undefined;
  if (disabled && checked) {
    return <Check size="25px" />;
  }
  return isProcessing ? <Loading /> : (
    <input
      type="checkbox"
      className="border-2 border-neutral-800"
      style={{ width: '25px', height: '25px' }}
      checked={checked}
      disabled={disabled}
      onChange={onChange}
    />
  );
}

export default function GroupPermissionsModal({ group, onClose }: { group: GroupWithUsers; onClose: () => void }) {
  const { data, error, isLoading, reload } = useGet<GroupPermissions>({
    endpoint: backendEndpoints.getGroupPermissions(group.id),
    smoothRefresh: false
  });

  return (
    <Modal onHide={onClose} show>
      <Modal.Header closeButton>
        <Modal.Title>Manage permissions</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-5">
        {error && <Alert>{error}</Alert>}
        {isLoading && <Loading />}
        {typeof data === 'object' && data && (
          <>
            <PermissionTable header="Roles">
              <tr>
                <td>{getRoleLabel(GroupRole.Manager)}</td>
                <td className="text-center">
                  <PermissionCheckbox checked disabled />
                </td>
                <td className="text-center">
                  <PermissionCheckbox checked disabled />
                </td>
              </tr>
              <tr>
                <td>{getRoleLabel(GroupRole.Member)}</td>
                <td className="text-center">
                  <PermissionCheckbox checked disabled />
                </td>
                <td className="text-center">
                  <PermissionCheckbox
                    checked={data.per_role[GroupRole.Member]['edit-group-file']}
                    groupId={group.id}
                    data={{
                      role: GroupRole.Member,
                      ability: BouncerAbility.EditGroupFile,
                    }}
                    reloadCallback={reload}
                  />
                </td>
              </tr>
            </PermissionTable>
            <PermissionTable header={concepts.Users}>
              {group?.users?.map((user) => {
                const userRoleInGroup = user.pivot.role;
                const permissionViaRole = data.per_role[userRoleInGroup];
                return (
                  <tr key={user.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <ProfilePicture user={user} size="40px"/>
                        <span>{getFullName(user)}</span>
                      </div>
                    </td>
                    <td className="text-center align-middle">
                      <OverlayTrigger
                        placement="top"
                        defaultShow={false}
                        overlay={
                          <Tooltip id={`via-role-${BouncerAbility.EditGroupFile}`}>
                            Included for everyone
                          </Tooltip>
                        }
                      >
                        <div>
                          <PermissionCheckbox checked disabled/>
                        </div>
                      </OverlayTrigger>
                    </td>
                    <td className="text-center align-middle">
                      <OverlayTrigger
                        placement="top"
                        defaultShow={false}
                        overlay={
                          <Tooltip id={`via-role-${BouncerAbility.EditGroupFile}`}>
                            Included via role ({getRoleLabel(userRoleInGroup).toLowerCase()})
                          </Tooltip>
                        }
                      >
                        <div>
                          <PermissionCheckbox
                            checked={data.per_user[user.id][BouncerAbility.EditGroupFile] || permissionViaRole[BouncerAbility.EditGroupFile]}
                            disabled={permissionViaRole[BouncerAbility.EditGroupFile]}
                            groupId={group.id}
                            data={{
                              user_id: user.id,
                              ability: BouncerAbility.EditGroupFile,
                            }}
                            reloadCallback={reload}
                          />
                        </div>
                      </OverlayTrigger>
                    </td>
                  </tr>
                );
              })}
            </PermissionTable>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose} variant="link">Cancel</Button>
      </Modal.Footer>
    </Modal>
  );
}
